import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PiggyBank, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Settings card: software renewal budget per quarter (amount + currency). */
export function LicenceBudgetSection({ isAdmin }) {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["reminder-settings"], queryFn: api.getReminderSettings });
  const { data: status } = useQuery({ queryKey: ["licence-budget"], queryFn: api.licenceBudgetStatus });
  const [amount, setAmount] = useState("");
  const [ccy, setCcy] = useState("USD");
  useEffect(() => {
    if (settings) {
      setAmount(settings.licence_budget_quarter ? String(settings.licence_budget_quarter) : "");
      setCcy(settings.licence_budget_currency || "USD");
    }
  }, [settings]);

  const saveMut = useMutation({
    mutationFn: () => api.updateReminderSettings({
      licence_budget_quarter: Number(amount || 0), licence_budget_currency: ccy.trim().toUpperCase(),
    }),
    onSuccess: () => {
      toast.success("Renewal budget saved");
      qc.invalidateQueries({ queryKey: ["reminder-settings"] });
      qc.invalidateQueries({ queryKey: ["licence-budget"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  return (
    <section className="border border-border rounded-md bg-white p-5 mb-6" data-testid="licence-budget-section">
      <h2 className="font-display font-semibold text-base flex items-center gap-2">
        <PiggyBank className="size-4 text-primary" /> Software renewal budget
      </h2>
      <p className="text-xs text-muted-foreground mt-1 mb-4">
        Budget per calendar quarter for licence renewals. The dashboard warns when next quarter&apos;s renewals
        exceed it. Set to 0 to disable the alert.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="overline">Budget per quarter</Label>
          <Input data-testid="licence-budget-amount" type="number" min="0" step="0.01" className="w-44"
                 value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!isAdmin} placeholder="0.00" />
        </div>
        <div>
          <Label className="overline">Currency</Label>
          <Input data-testid="licence-budget-currency" className="w-24 uppercase" maxLength={3}
                 value={ccy} onChange={(e) => setCcy(e.target.value)} disabled={!isAdmin} />
        </div>
        {isAdmin && (
          <Button data-testid="licence-budget-save" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
            {saveMut.isPending ? "Saving…" : "Save budget"}
          </Button>
        )}
        {status && (
          <span className="text-xs text-muted-foreground ml-auto" data-testid="licence-budget-preview">
            {status.quarter.label} renewals so far: <b className="mono text-foreground">{fmtMoney(status.forecast_total)} {status.currency}</b>
            {" "}({status.renewal_count} licence{status.renewal_count === 1 ? "" : "s"})
          </span>
        )}
      </div>
    </section>
  );
}

/** Dashboard tile + Licences banner. `variant` = "tile" | "banner". Hidden when no budget and no renewals. */
export function LicenceBudgetAlert({ variant = "tile" }) {
  const { data } = useQuery({ queryKey: ["licence-budget"], queryFn: api.licenceBudgetStatus });
  if (!data || (!data.budget && !data.renewal_count)) return null;
  const over = data.over_budget;
  const hasBudget = data.budget > 0;
  const pct = hasBudget ? Math.min(100, Math.round((data.ratio || 0) * 100)) : 0;
  const tone = over ? "red" : hasBudget && pct >= 80 ? "amber" : "emerald";
  const palette = {
    red: "bg-red-600 border-red-700 text-white",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[tone];
  const headline = hasBudget
    ? `${fmtMoney(data.forecast_total)} of ${fmtMoney(data.budget)} ${data.currency}`
    : `${fmtMoney(data.forecast_total)} ${data.currency}`;
  const sub = over
    ? `Over budget by ${fmtMoney(data.forecast_total - data.budget)} ${data.currency} · ${data.renewal_count} renewal${data.renewal_count === 1 ? "" : "s"}`
    : `${data.renewal_count} renewal${data.renewal_count === 1 ? "" : "s"} due in ${data.quarter.label}${hasBudget ? ` · ${pct}% of budget` : " · no budget set"}`;

  if (variant === "banner") {
    return (
      <div className={`border rounded-md px-4 py-3 mb-4 flex flex-wrap items-center gap-3 ${palette}`} data-testid="licence-budget-banner">
        {over ? <AlertTriangle className="size-4" /> : <CheckCircle2 className="size-4" />}
        <div className="text-sm">
          <b>Renewals {data.quarter.label}:</b> {headline}
          <span className={`text-xs ml-2 ${over ? "text-red-100" : "opacity-75"}`}>{sub}</span>
        </div>
        <Link to="/settings" className={`ml-auto text-xs underline ${over ? "text-red-100" : ""}`} data-testid="licence-budget-banner-settings">
          Adjust budget
        </Link>
      </div>
    );
  }
  return (
    <Link to="/licences" data-testid="stat-licence-budget"
          className={`rounded-md p-5 flex flex-col gap-3 border transition-colors hover:opacity-95 ${palette}`}>
      <div className="flex items-start justify-between">
        <p className={`overline ${over ? "!text-red-100" : ""}`}>Renewals {data.quarter.label}</p>
        <div className={`size-8 grid place-items-center rounded-md ${over ? "bg-white/15" : "bg-white/70"}`}>
          {over ? <AlertTriangle className="size-4" strokeWidth={2.25} /> : <PiggyBank className="size-4" strokeWidth={2.25} />}
        </div>
      </div>
      <div className="font-display text-2xl font-bold tracking-tight leading-none mono">{headline}</div>
      <p className={`text-xs ${over ? "text-red-100" : "opacity-75"}`}>
        {sub} <span className="inline-flex items-center gap-1 ml-1 underline">Licences <ArrowRight className="size-3" /></span>
      </p>
    </Link>
  );
}
